import styled, {css, keyframes} from "styled-components";
import image from './../assets/button_ui.png';
import {Home} from "../svg/Home";
import {Retry} from "../svg/Retry";
import {Heart} from "./Heart";
import {SoundOff, SoundOn} from "../svg/Sound";
import ru from './../assets/ru.jpg';
import en from './../assets/en.jpg';
import tr from './../assets/tk.png';
import {Play} from "../svg/Play";
import {Settings} from "../svg/Settings";
import {LeaderBord} from "../svg/LeaderBord";
import {Chevron} from "../svg/Chevron";
import {Question} from "../svg/Question";
import {Idea} from "../svg/Idea";
import {useSoundToggle} from "../hooks/useSoundToggle";
import bomb from './../assets/bomb/bombAnimation/3.png';

type DirectionType = 'left' | 'right' | 'bottom' | 'top' | 'initial'

const icons = {
    home: Home,
    retry: Retry,
    heart: Heart,
    soundOff: SoundOff,
    soundOn: SoundOn,
    ru,
    en,
    tr,
    play: Play,
    settings: Settings,
    leaderBord: LeaderBord,
    chevron: Chevron,
    question: Question,
    idea: Idea,
}

const dropShadowAnimation = keyframes`
    0% {
        filter: drop-shadow(0px 0px 8px gold);
    }
    50% {
        filter: drop-shadow(0px 0px 4px gold);
    }
    100% {
        filter: drop-shadow(0px 0px 8px gold);
    }
`;

const Root = styled.div<{isAnimateShine?: boolean}>`
    padding: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    cursor: pointer;
    animation: ${({isAnimateShine}) => isAnimateShine ? css`${dropShadowAnimation} 2s infinite` : 'none'};
`;

const RootText = styled.div<{isAnimateShine?: boolean}>`
    padding: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    cursor: pointer;
    animation: ${({isAnimateShine}) => isAnimateShine ? css`${dropShadowAnimation} 2s infinite` : 'none'};

    @media (min-width: 768px) {
        padding: 24px;
    }
`;

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const FilledGround = styled.img`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: -1;
    height: 100%;
    width: 100%;
    //filter: hue-rotate(45deg);

    -webkit-user-select: none; /* Safari */
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-tap-highlight-color: transparent;

    pointer-events: none; /* Prevent any interactions */
    user-drag: none; /* Disable image dragging */
    -webkit-user-drag: none; /* Disable image dragging for Safari */
`;

const Text = styled.div<{textColor?: string}>`
    color: #1c188c;
    font-weight: 900;
    font-size: 18px;
    ${({textColor}) => textColor && `color: ${textColor}`};
    
    @media (min-width: 768px) {
        font-size: 22px;
    }
    
    @media (min-width: 1024px) {
        font-size: 40px;
    }
`;

const Svg = styled.div<{ size: number; fill: string; direction: DirectionType }>`
    width: ${({size}) => size}px;
    height: ${({size}) => size}px;
    fill: ${({fill}) => fill};
    transform: ${({direction}) => {
        if (direction === 'left') return 'rotate(180deg)';
        if (direction === 'top') return 'rotate(270deg)';
        if (direction === 'bottom') return 'rotate(90deg)';
        return 'rotate(0)';
    }};

    @media (min-width: 768px) {
        padding: 10px;
    }
`;

const TextWrapper = styled.div`
    width: 60%;
    font-size: 10px;
    line-height: 10px;
    text-align: center;
    font-weight: 900;
    color: #1c188c;

    @media (min-width: 768px) {
        line-height: 16px;
        font-size: 16px;
    }

    @media (min-width: 1024px) {
        line-height: 22px;
        font-size: 22px;
    }
`;

const TextPlay = styled.div`
    width: 60%;
    font-size: 18px;
    line-height: 18px;
    text-align: center;
    font-weight: 900;
    color: #1c188c;
`;

const AnimateImage = styled.div`
    width: 45px;
    height: 35px;
    
    @media (min-width: 768px) {
        width: 70px;
        height: 55px;
    }

    @media (min-width: 1024px) {
        width: 90px;
        height: 70px;
    }
`;

const Image = styled.img<{ size: number; padding?: number; sizeImageWidth?: number }>`
    width: ${({size, sizeImageWidth}) => sizeImageWidth || size}px;
    height: ${({size}) => size}px;
    padding: ${({padding}) => padding}px;
    
    @media (min-width: 1024px) {
        padding: 8px;
    }

    -webkit-user-select: none; /* Safari */
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-tap-highlight-color: transparent;

    pointer-events: none; /* Prevent any interactions */
    user-drag: none; /* Disable image dragging */
    -webkit-user-drag: none; /* Disable image dragging for Safari */
`;

interface Props {
    text?: string;
    iconName?: keyof typeof icons;
    size?: number;
    color?: string;
    onClick?: () => void;
    direction?: DirectionType;
    padding?: number;
    sizeImageWidth?: number;
    isAnimateShine?: boolean;
    textColor?: string;
}

export const Button = ({text, iconName, color, size, onClick, direction = 'initial', padding, sizeImageWidth, isAnimateShine, textColor}: Props) => {
    const {playSound} = useSoundToggle('click_button');

    if (iconName && ['ru', 'en', 'tr'].includes(iconName as string)) {
        const src = icons[iconName] as string
        return (
            <Root isAnimateShine={isAnimateShine} onClick={() => {
                onClick?.();
                playSound();
            }}>
                <FilledGround src={image}/>
                {size && <Image sizeImageWidth={sizeImageWidth} padding={padding} size={size} src={src} />}
            </Root>
        )
    }

    if (iconName === 'heart' && text && size) {
        return (
            <Root isAnimateShine={isAnimateShine} onClick={() => {
                onClick?.();
                playSound();
            }}>
                <Container>
                    <FilledGround src={image}/>
                    <AnimateImage>
                        <Heart />
                    </AnimateImage>
                    <TextWrapper>
                        {text.toUpperCase()}
                    </TextWrapper>
                </Container>
            </Root>
        )
    }

    if (iconName === 'play' && text) {
        const Component = icons[iconName]
        return (
            <Root isAnimateShine={isAnimateShine} onClick={() => {
                onClick?.();
                playSound();
            }}>
                <FilledGround src={image}/>
                <TextPlay>
                    {text.toUpperCase()}
                </TextPlay>
                {size && color && <Svg direction={direction} size={size} fill={color}><Component/></Svg>}
            </Root>
        )
    }

    if (iconName && !text) {
        const Component = icons[iconName]
        return (
            <Root isAnimateShine={isAnimateShine} onClick={() => {
                onClick?.();
                playSound();
            }}>
                <FilledGround src={image}/>
                {size && color && <Svg direction={direction} size={size} fill={color}><Component/></Svg>}
            </Root>
        )
    }

    return (
        <RootText isAnimateShine={isAnimateShine} onClick={() => {
            onClick?.();
            playSound();
        }}>
            <FilledGround src={image}/>
            <Text textColor={textColor}>{text}</Text>
        </RootText>
    )
}
